import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { FaSearch } from 'react-icons/fa';
import { U2000AutoCompleteSearchCountFooter } from '../../../common/U2000AutoCompleteSearchCountFooter';
import {
  U2000_CRITERIA_LOOKUP_PAGE_SIZE,
  type U2000CriteriaLookupSearchCriterion,
} from './U2000CriteriaLookupRegistry';
import type { U2000AutoCompleteSearchResult } from '../../../../types/U2000AutoCompleteSearchResult';
import { u2000CapAutoCompleteSearchResults } from '../../../../utils/u2000CapAutoCompleteSearchResults';
import { u2000NormalizeAutoCompleteSearchResult } from '../../../../utils/u2000NormalizeAutoCompleteSearchResult';
import { u2000ShouldShowAutoCompleteDropdown } from '../../../../utils/u2000ShouldShowAutoCompleteDropdown';

const INPUT_BASE =
  'w-full min-h-[46px] pl-4 pr-10 py-2 text-sm bg-surface border rounded-lg transition-all duration-200 border-gray-300 hover:border-gray-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-hidden disabled:cursor-not-allowed disabled:opacity-70';

function SearchIconSpinner() {
  return (
    <svg className="h-4 w-4 animate-spin text-gray-400" viewBox="0 0 24 24" aria-hidden>
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

export type U2000CriteriaAutoCompleteProps<TItem> = {
  value: string;
  displayValue?: string;
  onSelect: (value: string, displayValue: string) => void;
  onSearch: (
    criterias: U2000CriteriaLookupSearchCriterion[],
  ) => Promise<TItem[] | U2000AutoCompleteSearchResult<TItem>>;
  itemToValue: (item: TItem) => string;
  itemToDisplay: (item: TItem) => string;
  /** Libellé dans le champ après sélection (défaut : itemToDisplay) */
  itemToSelectedDisplay?: (item: TItem) => string;
  searchCriterionField: string;
  pageSize?: number;
  disabled?: boolean;
  placeholder?: string;
  searchingLabel?: string;
  noResultsLabel?: (query: string) => string;
  renderSuggestion?: (item: TItem) => ReactNode;
  /** Appelé après sélection clavier (Tab/Enter) pour avancer le focus */
  onAfterSelect?: () => void;
};

function U2000CriteriaAutoComplete<TItem>({
  value,
  displayValue,
  onSelect,
  onSearch,
  itemToValue,
  itemToDisplay,
  itemToSelectedDisplay,
  searchCriterionField,
  pageSize = U2000_CRITERIA_LOOKUP_PAGE_SIZE,
  disabled = false,
  placeholder,
  searchingLabel = '…',
  noResultsLabel = (query) => query,
  renderSuggestion,
  onAfterSelect,
}: U2000CriteriaAutoCompleteProps<TItem>) {
  const searchRef = useRef(onSearch);
  searchRef.current = onSearch;
  const [inputValue, setInputValue] = useState(displayValue ?? value ?? '');
  const [isFocused, setIsFocused] = useState(false);
  const [suggestions, setSuggestions] = useState<TItem[]>([]);
  const [searchMeta, setSearchMeta] = useState<{ totalCount?: number; truncated?: boolean }>({});
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  /** True après une recherche terminée pour la saisie courante (y compris 0 résultat). */
  const [searchSettled, setSearchSettled] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [dropdownClosed, setDropdownClosed] = useState(false);
  const debounceRef = useRef<number | null>(null);
  const lastSearchedQueryRef = useRef('');
  const requestIdRef = useRef(0);

  useEffect(() => {
    setInputValue(displayValue ?? value ?? '');
    if (value) {
      setDropdownClosed(true);
    }
  }, [value, displayValue]);

  useEffect(() => {
    if (debounceRef.current != null) {
      window.clearTimeout(debounceRef.current);
    }
    const query = inputValue.trim();
    if (!query || disabled || !isFocused || dropdownClosed) {
      lastSearchedQueryRef.current = '';
      setSuggestions([]);
      setSearchMeta({});
      setLoadingSuggestions(false);
      setHighlightIndex(-1);
      setSearchSettled(false);
      return;
    }
    setSearchSettled(false);
    debounceRef.current = window.setTimeout(async () => {
      if (query === lastSearchedQueryRef.current) return;
      lastSearchedQueryRef.current = query;
      const requestId = ++requestIdRef.current;
      setLoadingSuggestions(true);
      try {
        const raw = await searchRef.current([
          { fieldName: searchCriterionField, value: query },
          { fieldName: 'pageSize', value: String(pageSize) },
        ]);
        if (requestId !== requestIdRef.current) return;
        const normalized = u2000CapAutoCompleteSearchResults(
          u2000NormalizeAutoCompleteSearchResult(raw),
          pageSize,
        );
        setSuggestions(normalized.results);
        setSearchMeta({ totalCount: normalized.totalCount, truncated: normalized.truncated });
        setHighlightIndex(normalized.results.length > 0 ? 0 : -1);
        setSearchSettled(true);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setSuggestions([]);
        setSearchMeta({});
        setHighlightIndex(-1);
        setSearchSettled(true);
      } finally {
        if (requestId === requestIdRef.current) {
          setLoadingSuggestions(false);
        }
      }
    }, 300);
    return () => {
      if (debounceRef.current != null) {
        window.clearTimeout(debounceRef.current);
      }
      requestIdRef.current += 1;
    };
  }, [inputValue, disabled, isFocused, dropdownClosed, searchCriterionField, pageSize]);

  const closeDropdown = useCallback(() => {
    if (debounceRef.current != null) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    requestIdRef.current += 1;
    setDropdownClosed(true);
    setSuggestions([]);
    setSearchMeta({});
    setLoadingSuggestions(false);
    setSearchSettled(false);
    setHighlightIndex(-1);
  }, []);

  const selectItem = useCallback(
    (item: TItem, advanceFocus = false) => {
      const v = itemToValue(item);
      const d = itemToSelectedDisplay ? itemToSelectedDisplay(item) : itemToDisplay(item);
      if (!v) return;
      onSelect(v, d);
      setInputValue(d);
      lastSearchedQueryRef.current = d;
      closeDropdown();
      if (advanceFocus) {
        window.setTimeout(() => onAfterSelect?.(), 0);
      }
    },
    [itemToValue, itemToDisplay, itemToSelectedDisplay, onSelect, closeDropdown, onAfterSelect],
  );

  const showDropdown = u2000ShouldShowAutoCompleteDropdown({
    isFocused,
    disabled,
    queryReady: inputValue.trim().length > 0,
    dropdownClosed,
    loading: loadingSuggestions,
    suggestionCount: suggestions.length,
    searchSettled,
  });

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const highlightedItem = highlightIndex >= 0 ? suggestions[highlightIndex] : undefined;

    if (e.key === 'ArrowDown' && suggestions.length > 0) {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % suggestions.length);
      return;
    }
    if (e.key === 'ArrowUp' && suggestions.length > 0) {
      e.preventDefault();
      setHighlightIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
      return;
    }
    if ((e.key === 'Enter' || e.key === 'Tab') && showDropdown && highlightedItem) {
      e.preventDefault();
      selectItem(highlightedItem, true);
      return;
    }
    if (e.key === 'Escape') {
      closeDropdown();
    }
  };

  return (
    <div className="relative w-full">
      <input
        type="text"
        role="combobox"
        className={INPUT_BASE}
        value={inputValue}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(e) => {
          setDropdownClosed(false);
          lastSearchedQueryRef.current = '';
          setInputValue(e.target.value);
          if (value) {
            onSelect('', '');
          }
        }}
        onFocus={() => {
          setIsFocused(true);
          if (value) {
            setDropdownClosed(true);
          } else {
            setDropdownClosed(false);
          }
        }}
        onBlur={(e) => {
          const related = e.relatedTarget as Node | null;
          const root = (e.currentTarget as HTMLElement).closest('.relative.w-full');
          if (related != null && root?.contains(related)) {
            return;
          }
          window.setTimeout(() => {
            const active = document.activeElement;
            if (active != null && root?.contains(active)) return;
            setIsFocused(false);
          }, 150);
        }}
        onKeyDown={handleKeyDown}
        aria-autocomplete="list"
        aria-expanded={showDropdown}
      />
      <span
        className={`pointer-events-none absolute top-1/2 right-3 z-10 -translate-y-1/2 ${disabled ? 'text-gray-300' : 'text-gray-400'}`}
        aria-hidden
      >
        {loadingSuggestions ? <SearchIconSpinner /> : <FaSearch className="h-4 w-4" />}
      </span>
      {showDropdown && (
        <div
          className="absolute left-0 right-0 top-full z-50 mt-1 flex max-h-64 min-h-0 flex-col rounded-lg border border-gray-200 bg-white shadow-lg"
          onMouseDown={(e) => e.preventDefault()}
        >
          <ul role="listbox" className="min-h-0 flex-1 overflow-y-auto py-1">
            {loadingSuggestions && <li className="px-3 py-2 text-sm text-gray-500">{searchingLabel}</li>}
            {!loadingSuggestions && suggestions.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-600">{noResultsLabel(inputValue.trim())}</li>
            )}
            {!loadingSuggestions &&
              suggestions.map((item, idx) => (
                <li key={idx} role="option" aria-selected={idx === highlightIndex}>
                  <button
                    type="button"
                    className={`w-full px-3 py-2 text-left text-sm hover:bg-blue-50 ${idx === highlightIndex ? 'bg-blue-50' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectItem(item)}
                  >
                    {renderSuggestion ? renderSuggestion(item) : itemToDisplay(item)}
                  </button>
                </li>
              ))}
          </ul>
          {!loadingSuggestions && (
            <U2000AutoCompleteSearchCountFooter
              displayedCount={suggestions.length}
              totalCount={searchMeta.totalCount}
            />
          )}
        </div>
      )}
    </div>
  );
}

export default U2000CriteriaAutoComplete;
